import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  params, string, shape, number, client, extend, useDataModel, arrayOf
} from '@thd-nucleus/data-sources';
import { withDynamicImport } from '@thd-nucleus/app-render';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { useStoreId, ExperienceContext } from '@thd-nucleus/experience-context';
import { LoadingIconButton, LoadingButton } from '@one-thd/sui-atomic-components';
import { Add, Heart } from '@one-thd/sui-icons';
import { TRACK } from './constants';
import { AddToListProvider } from './AddToListProvider';
import { AddToListDrawer } from './drawers/AddToListDrawer';

const ImportedAddToListDrawer = withDynamicImport({
  server: () => {
    return AddToListDrawer;
  },
  client: () => {
    return import('./drawers/AddToListDrawer');
  },
  name: 'AddToListDrawer',
});

const AddToList = (props) => {
  const {
    itemId,
    quantity,
    CustomAddToListActionElement,
    products,
    skipListId,
    isSharedList,
    showCount,
    showIconButton,
    storeId: propStoreId,
    isMultiSelect
  } = props;

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const [drawerState, setDrawerState] = useState({ drawerOpen: false, drawerLoading: false });
  const {
    drawerOpen,
    drawerLoading
  } = drawerState;
  const [favoriteCount, setFavoriteCount] = useState(0);
  const theme = useTheme(AddToList, props);
  const { hideCount, showPlusSign } = theme.props;
  const defaultStoreId = useStoreId();
  const storeId = propStoreId || defaultStoreId;

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('add-to-list.ready');
  }, []);

  const { data } = useDataModel('product', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId
  });

  const productData = data?.product || {};

  const favoriteDetailCount = productData?.favoriteDetail?.count;

  useEffect(() => {
    setFavoriteCount(favoriteDetailCount);
  }, [favoriteDetailCount]);

  const count = favoriteCount || favoriteDetailCount;
  const canShowCount = !hideCount && showCount && count;
  let favoriteCountRoundOff = count;
  const maxFavoriteCount = 999;
  if (count && count > maxFavoriteCount) {
    favoriteCountRoundOff = `${(count / 1000).toFixed(1)}k`;
  }

  const handleDrawerLoading = (loading) => {
    setDrawerState((prevState) => {
      return {
        ...prevState,
        drawerLoading: loading
      };
    });
  };

  const closeDrawer = () => {
    setDrawerState({
      drawerOpen: false,
      drawerLoading: false
    });
  };

  const openDrawer = () => {
    if (isMobile && typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS?.trigger) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('add-to.mobile');
    }
    setDrawerState({
      drawerOpen: true,
      drawerLoading: true
    });

    if (window.digitalData?.page?.pageInfo?.pageType === 'pip') {
      window.LIFE_CYCLE_EVENT_BUS.trigger(TRACK.BUTTON_CLICK, {});
    }
    LIFE_CYCLE_EVENT_BUS.trigger(TRACK.OPEN_DRAWER, {});
  };

  const favButtonWrapperClass = classNames({
    'sui-pr-2': canShowCount
  });

  return (
    <span data-component="AddToList">
      {showIconButton
        ? (
          <div className={favButtonWrapperClass}>
            <>
              <LoadingIconButton
                aria-label="Add To List"
                size="large"
                icon={showPlusSign ? Add : Heart}
                onClick={openDrawer}
                loading={drawerLoading}
              />
              { canShowCount && (<span>{favoriteCountRoundOff}</span>)}
            </>
          </div>
        ) : (
          <>
            {CustomAddToListActionElement
              ? (
                <CustomAddToListActionElement onClick={openDrawer} />
              ) : (
                <LoadingButton
                  variant="secondary"
                  fullWidth
                  onClick={openDrawer}
                  loading={drawerLoading}
                >
                  Add to List
                </LoadingButton>
              )}
          </>
        )}
      {
        drawerOpen && (
          <AddToListProvider productData={productData}>
            <ImportedAddToListDrawer
              itemId={itemId}
              quantity={quantity}
              products={products}
              open={drawerOpen}
              onClose={closeDrawer}
              skipListId={skipListId}
              isSharedList={isSharedList}
              setFavoriteCount={setFavoriteCount}
              favoriteCount={favoriteCount}
              storeId={storeId}
              drawerLoadingHandler={handleDrawerLoading}
              isMultiSelect={isMultiSelect}
            />
          </AddToListProvider>
        )
      }
    </span>
  );
};

AddToList.propTypes = {
  itemId: PropTypes.string,
  skipListId: PropTypes.string,
  isSharedList: PropTypes.bool,
  quantity: PropTypes.number,
  CustomAddToListActionElement: PropTypes.func,
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    price: PropTypes.number
  })),
  showCount: PropTypes.bool,
  showIconButton: PropTypes.bool,
  storeId: PropTypes.string,
  isMultiSelect: PropTypes.bool
};

AddToList.defaultProps = {
  itemId: null,
  skipListId: null,
  quantity: 1,
  CustomAddToListActionElement: null,
  products: [],
  isSharedList: false,
  showCount: false,
  showIconButton: false,
  storeId: null,
  isMultiSelect: null
};

AddToList.themeProps = {
  hideCount: PropTypes.bool,
  showPlusSign: PropTypes.bool
};

AddToList.defaultThemeProps = {
  hideCount: false,
  showPlusSign: false
};

AddToList.displayName = 'AddToList';

const favoriteDetailCount = shape({
  count: number()
});

if (favoriteDetailCount.skip) {
  favoriteDetailCount.skip('skipFavoriteCount', false);
}

AddToList.dataModel = extend(
  {
    product: params({ itemId: string().isRequired(), dataSource: string() }).shape({
      itemId: string(),
      dataSource: string(),
      dataSources: string(),
      favoriteDetail: client(favoriteDetailCount),
      pricing: params({ storeId: string() }).shape({
        value: number({ float: true })
      }),
      identifiers: shape({
        brandName: string(),
        productLabel: string()
      }),
      media: shape({
        images: arrayOf(shape({
          url: string(),
          type: string(),
          subType: string()
        }))
      }),
    })
  },
  AddToListDrawer
);

export { AddToList };
